import { FormInputNumber, FormSelectInline } from '@systemeio/ui'
import React, { memo, useMemo } from 'react'
import {
  FieldErrorAndDescription,
  FieldErrorAndDescriptionProps,
} from 'shared/components/form/field-error-and-description'
import { FieldLabel, FieldLabelProps } from 'shared/components/form/field-label'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { TimeType, TimeTypeState } from './constants'
import { createTimeTypeOptionsForSelect } from './utils'

export type TimeDelayPickerProps = Omit<
  React.HTMLProps<HTMLLabelElement>,
  'onChange' | 'label' | 'max'
> &
  FieldLabelProps &
  FieldErrorAndDescriptionProps & {
    selectedTimeValue: number
    selectedTimeType: TimeType
    selectedTimeBeforeAfter?: TimeTypeState
    onChange: (
      selectedTimeValue: TimeDelayPickerProps['selectedTimeValue'],
      selectedTimeType: TimeDelayPickerProps['selectedTimeType'],
      selectedTimeBeforeAfter?: TimeDelayPickerProps['selectedTimeBeforeAfter'],
    ) => void
    classNameInputInteger?: string
    withTimeAfterBeforeSelect?: boolean
    isPreFetching?: boolean
    allowedOptions?: TimeType[]
    max?: number
  }

const TimeDelayPicker = memo(function TimeDelayPicker({
  description,
  error,
  selectedTimeValue,
  selectedTimeType,
  onChange,
  labelClassName,
  className,
  label,
  required,
  selectedTimeBeforeAfter,
  withTimeAfterBeforeSelect,
  classNameInputInteger,
  isPreFetching,
  allowedOptions,
  disabled,
  max,
  ...props
}: TimeDelayPickerProps) {
  const { t, tICU } = useLocoTranslation()

  const timeStates = [
    { id: TimeTypeState.before, caption: t('dashboard.funnel.emails_webinar_start_before') },
    { id: TimeTypeState.after, caption: t('dashboard.funnel.emails_webinar_start_after') },
  ]

  const typeTypeOptions = useMemo(
    () => createTimeTypeOptionsForSelect(tICU, selectedTimeValue, allowedOptions),
    [allowedOptions, tICU, selectedTimeValue],
  )

  return (
    <label {...props} className={`flex flex-col gap-1 text-sm font-medium ${className}`}>
      <FieldLabel label={label} labelClassName={labelClassName} required={required} />
      <div className={`flex items-center justify-start gap-4 `}>
        {withTimeAfterBeforeSelect && (
          <FormSelectInline
            disabled={disabled}
            withoutCloseIcon
            data={timeStates}
            isPreFetching={isPreFetching}
            value={selectedTimeBeforeAfter}
            onChange={value =>
              onChange(selectedTimeValue, selectedTimeType, value as TimeTypeState)
            }
          />
        )}
        <FormInputNumber
          disabled={disabled}
          className={`w-3/12 ${classNameInputInteger}`}
          value={selectedTimeValue}
          onChange={value => onChange(value, selectedTimeType, selectedTimeBeforeAfter)}
          isPreFetching={isPreFetching}
          type={'integer'}
          max={max}
        />
        <FormSelectInline
          disabled={disabled}
          withoutCloseIcon
          data={typeTypeOptions}
          value={selectedTimeType}
          isPreFetching={isPreFetching}
          onChange={value =>
            onChange(selectedTimeValue, value as TimeType, selectedTimeBeforeAfter)
          }
        />
      </div>
      <FieldErrorAndDescription description={description} error={error} />
    </label>
  )
})

export default TimeDelayPicker
